import React, { useState } from 'react';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { deleteReview, createReply, deleteReply, updateReply } from 'api/TrailReviews';
import Review from 'types/Review';
import { Trail } from 'types/Trails';
import logError from 'utils/logError';
import { logTrailDetailsActivityViewed, TrailDetailsActivityLocations } from 'utils/trail_analytics_helpers';
import AdminReplyItem from 'react_components/feeds/AdminReplyItem';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { wrapUrlSafe } from 'utils/language_support_util';
import OriginalLanguageButton from 'components/shared/OriginalLanguageButton';
import FirstToReviewBadge from 'images/icons/first-to-review.svg';
import useUser from 'hooks/useUser';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import * as styles from './styles/styles.module.scss';
import TrailUgcCardContent from '../TrailUgcCardContent';
import EditDeleteGroup from './EditDeleteGroup';
import useLocalizedText from 'hooks/useLocalizedText';

export type TrailReviewResultCardProps = {
  review: Review;
  className?: string;
  detailsClassName?: string;
  hideTags?: boolean;
  humanizeTime?: boolean;
  includeTrailName?: boolean;
  preferHTML?: boolean;
  onReviewDeleted?: (data: Review, trail: Trail) => void;
  openReviewFormModal?: (trailFormProps: any) => void;
  updateReview?: (data: { detail: Review }) => void;
};

const TrailReviewResultCard = ({
  onReviewDeleted,
  openReviewFormModal,
  updateReview,
  review,
  includeTrailName = false,
  hideTags,
  humanizeTime,
  preferHTML,
  className,
  detailsClassName
}: TrailReviewResultCardProps) => {
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [showReplyEditing, setShowReplyEditing] = useState(false);
  const sourceText = review?.comment_source || review?.comment;
  const {
    isTranslatable,
    toggleText,
    toggledLocaleText,
    localizedText,
  } = useLocalizedText(sourceText, review?.comment_lang, 'reviews', 'comment', `${review?.id}`);

  const handleOpenReviewFormModal = (e: React.MouseEvent) => {
    e.preventDefault();
    openReviewFormModal?.({ ...review });
  };

  const recordingLink = () => {
    if (review.associatedRecording && review.user && (user?.admin || review.associatedRecording.contentPrivacy !== 'private')) {
      const recording = review.associatedRecording;
      const viewUsersRecording = StringConstantsMixin.strings.VIEW_USERS_RECORDING.replace('{userName}', review.user.firstName);
      return (
        <a
          className={`${styles.link} ${styles.recording} ${StringConstantsMixin.constants.XLATE.NONE}`}
          href={wrapUrlSafe(`/explore/recording/${recording.slug}`, languageRegionCode)}
          target="_blank"
          onClick={() => logTrailDetailsActivityViewed(TrailDetailsActivityLocations.Review, recording.id, review.id.toString(), review.trailId)}
          rel="noreferrer"
        >
          {viewUsersRecording}
        </a>
      );
    }
    return null;
  };

  const handleAdminReplyClick = () => {
    setShowReplyEditing(true);
  };

  const handleDeleteReviewClick = () => {
    if (!window.confirm('Are you sure you want to delete this review?')) return false;
    deleteReview(review)
      .then(response => {
        if (onReviewDeleted && response.trails && response.trails.length > 0) {
          onReviewDeleted(review, response.trails[0]);
        }
      })
      .catch(e => logError(`Failure: ${e}`));
    return false;
  };

  const actionsDiv = () => {
    let langButton = null;
    if (isTranslatable) {
      langButton = <OriginalLanguageButton active={toggledLocaleText} handleClick={toggleText} eventSource="Reviews Feed" />;
    }
    let editDeleteGroup = null;
    if (review.user.id === user?.id) {
      editDeleteGroup = <EditDeleteGroup editable onButtonClick={handleDeleteReviewClick} onLinkClick={handleOpenReviewFormModal} />;
    } else if (user?.admin) {
      editDeleteGroup = <EditDeleteGroup onButtonClick={handleDeleteReviewClick} onLinkClick={handleAdminReplyClick} />;
    }
    if (langButton || editDeleteGroup) {
      return (
        <div className={styles.actions}>
          {langButton}
          {editDeleteGroup}
        </div>
      );
    }
    return null;
  };

  const getTagNames = () => {
    if (hideTags) {
      return [];
    }

    let tags = [];
    if (review.activity) {
      tags.push(StringConstantsMixin.stringsLookup(StringConstantsMixin.strings.FILTERS.ACTIVITIES, review.activity.name));
    }
    if (review.obstacles) {
      const sortedObs = review.obstacles
        .map(obstacle => StringConstantsMixin.stringsLookup(StringConstantsMixin.strings.FILTERS.OBSTACLES, obstacle.name))
        .sort();
      tags = tags.concat(sortedObs);
    }
    return tags;
  };

  const handleReplyEditCancel = () => {
    setShowReplyEditing(false);
  };

  const changeRepliesSuccessCallback = (xhr: any) => {
    if (updateReview && xhr.trail_reviews && xhr.trail_reviews.length > 0) {
      setShowReplyEditing(false);
      updateReview({ detail: xhr.trail_reviews[0] });
    }
  };

  const handleDeleteReplyClick = () => {
    const { replies } = review;
    if (replies) {
      deleteReply(review)
        .then(response => {
          changeRepliesSuccessCallback(response);
        })
        .catch(e => logError(e));
    }
  };

  const handleReplySave = (textComment: string) => {
    const { replies } = review;
    if (replies && replies.length > 0) {
      updateReply(review, textComment)
        .then(changeRepliesSuccessCallback)
        .catch(e => logError(e));
    } else {
      createReply(review, textComment)
        .then(changeRepliesSuccessCallback)
        .catch(e => logError(e));
    }
  };

  const renderAdminRepliesSection = () => {
    if (showReplyEditing || review.replies) {
      // right now we only support 1 response, but this can be reworked to add more
      let existingReply;
      if (review.replies) {
        [existingReply] = review.replies;
      }
      return (
        <AdminReplyItem
          reply={existingReply}
          handleReplySave={handleReplySave}
          handleReplyEditCancel={handleReplyEditCancel}
          handleDeleteReplyClick={handleDeleteReplyClick}
          showReplyEditing={showReplyEditing}
          locale={languageRegionCode}
          adminUser={user?.admin}
        />
      );
    }

    return null;
  };

  const nameLinkSettings = () => {
    const { trailName, trailSlug } = review;
    if (!includeTrailName || !trailName || !trailSlug) {
      return null;
    }

    return {
      link: `/trail/${trailSlug}`,
      name: trailName,
      verb: StringConstantsMixin.strings.REVIEWED
    };
  };

  let detailsDiv = null;
  if (localizedText) {
    const classes = [detailsClassName, styles.details, StringConstantsMixin.constants.XLATE.GOOGLE];
    let firstReviewDiv = null;
    if (review.badges && review.badges.first) {
      classes.push(styles.firstToReview);
      firstReviewDiv = (
        <div className={styles.badgeHolder}>
          <img src={FirstToReviewBadge} />
          {StringConstantsMixin.strings.FIRST_TO_REVIEW}
        </div>
      );
    }
    let commentDiv = null;
    if (preferHTML && review.comment_html) {
      const sanitizeConfig = {
        ALLOWED_TAGS: ['strong'] // review.comment_html only supports bolding
      };
      const sanitizedHTML = DOMPurify.sanitize(review.comment_html, sanitizeConfig);
      commentDiv = <p itemProp="reviewBody" dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
    } else {
      commentDiv = <p itemProp="reviewBody">{localizedText}</p>;
    }
    detailsDiv = (
      <div className={classNames(classes)}>
        {firstReviewDiv}
        {commentDiv}
      </div>
    );
  }
  return (
    <div itemScope itemProp="review" itemType="http://schema.org/Review" className={classNames(styles.container, className)}>
      <TrailUgcCardContent
        className={styles.content}
        created_at={review.metadata.created}
        item={review}
        tagNames={getTagNames()}
        nameLinkSettings={nameLinkSettings()}
        humanizeTime={humanizeTime}
      />
      {detailsDiv}
      {renderAdminRepliesSection()}
      {recordingLink()}
      {actionsDiv()}
    </div>
  );
};

export default TrailReviewResultCard;
